import { Logo } from "@components/Logo";
import { OrgSwitcher } from "@features/organisations";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import {
  Box,
  ButtonBase,
  Drawer,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useChatStore } from "@stores/chatStore";
import { useLayoutStore } from "@stores/layoutStore";
import { useMatch, useNavigate, useRouterState } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

import {
  PEEK_CLOSE_DELAY_MS,
  PEEK_OPEN_DELAY_MS,
  SIDEBAR_RAIL_WIDTH,
  SIDEBAR_WIDTH,
} from "../constants";
import type { AppNavMode } from "../useLayoutMode";
import { ProjectSidebarContent } from "./ProjectSidebarContent";
import { SidebarCredits } from "./SidebarCredits";
import { SidebarNav } from "./SidebarNav";
import { SidebarRail } from "./SidebarRail";
import { SidebarUser } from "./SidebarUser";

interface SidebarProps {
  mode: AppNavMode;
  /** Drawer-mode only — bound to the temporary drawer's open state. */
  drawerOpen: boolean;
  onDrawerClose: () => void;
}

/**
 * Single Pin/Unpin toggle for the sidebar's brand row. Shows a filled
 * thumbtack when the user has pinned the sidebar expanded, an outlined
 * thumbtack otherwise. Click toggles between "expanded" (pinned) and
 * "auto" (follow route + breakpoint rules).
 */
function PinToggleButton() {
  const pin = useLayoutStore((s) => s.appNavPin);
  const setPin = useLayoutStore((s) => s.setAppNavPin);
  const isPinned = pin === "expanded";

  return (
    <Tooltip
      title={
        isPinned
          ? "Unpin sidebar (auto-collapse on chat)"
          : "Pin sidebar open everywhere"
      }
    >
      <IconButton
        size="small"
        onClick={() => setPin(isPinned ? "auto" : "expanded")}
        aria-label={isPinned ? "Unpin sidebar" : "Pin sidebar"}
        aria-pressed={isPinned}
        sx={{
          color: isPinned ? "primary.light" : "text.secondary",
          transition: (t) => `color ${t.motion.fast}`,
        }}
      >
        {isPinned ? (
          <PushPinIcon sx={{ fontSize: 18 }} />
        ) : (
          <PushPinOutlinedIcon sx={{ fontSize: 18 }} />
        )}
      </IconButton>
    </Tooltip>
  );
}

/**
 * Small mono ALL-CAPS section header — same vocabulary as the
 * project-card kicker / dialog eyebrow. Used to organise the sidebar
 * into clusters (workspace / navigate / account) rather than letting
 * the sections run together.
 */
function ClusterHeader({ label }: { label: string }) {
  return (
    <Typography
      sx={{
        fontFamily: (t) => t.fonts.mono,
        fontSize: "0.625rem",
        fontWeight: 500,
        color: "text.secondary",
        letterSpacing: "0.22em",
        textTransform: "uppercase",
        px: 0.75,
        mb: 0.5,
      }}
    >
      {label}
    </Typography>
  );
}

/**
 * Brand mark wrapped as a route-home affordance. Clicking sends the
 * user to `/projects` — the workspace index, the canonical "home" of
 * the app. Standard web convention; the tooltip surfaces the
 * destination so a confirmatory hover is enough for new users.
 */
function HomeLogo({ size, pulseEmber }: { size: number; pulseEmber: boolean }) {
  const navigate = useNavigate();
  return (
    <Tooltip title="Go to projects" enterDelay={400}>
      <ButtonBase
        onClick={() => void navigate({ to: "/projects" })}
        aria-label="Go to projects"
        focusRipple
        sx={(t) => ({
          borderRadius: 1,
          px: 0.5,
          py: 0.25,
          mx: -0.5,
          transition: `background-color ${t.motion.fast}, opacity ${t.motion.fast}`,
          "&:hover": { backgroundColor: t.palette.surface.raised },
          "&:active": { opacity: 0.85 },
          "&.Mui-focusVisible": {
            outline: `2px solid ${t.palette.editorial.main}`,
            outlineOffset: 2,
          },
        })}
      >
        <Logo size={size} pulseEmber={pulseEmber} />
      </ButtonBase>
    </Tooltip>
  );
}

function GlobalSidebarContent({ showPinToggle }: { showPinToggle?: boolean }) {
  // Pulse the brand mark's pin-1 ember while a chat is mid-stream so
  // the visual heartbeat stays visible even when the streaming row has
  // scrolled off-screen.
  const isStreaming = useChatStore((s) => s.isStreaming);
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        p: 2,
        gap: 2,
        overflowY: "auto",
        overflowX: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 0.5,
          py: 0.5,
          minHeight: 40,
        }}
      >
        <HomeLogo size={40} pulseEmber={isStreaming} />
        {showPinToggle && <PinToggleButton />}
      </Box>

      <Box>
        <ClusterHeader label="Workspace" />
        <OrgSwitcher />
      </Box>

      <Box>
        <ClusterHeader label="Navigate" />
        <SidebarNav />
      </Box>

      <Box sx={{ flexGrow: 1 }} />

      <Box
        sx={{
          mx: -2,
          mb: -2,
          px: 2,
          pt: 2,
          pb: 2,
          display: "flex",
          flexDirection: "column",
          gap: 1,
          borderTop: "1px solid",
          borderColor: "border.default",
          bgcolor: "surface.sunken",
        }}
      >
        <ClusterHeader label="Account" />
        <SidebarUser />
        <SidebarCredits />
      </Box>
    </Box>
  );
}

interface SidebarContentProps {
  showPinToggle?: boolean;
}

function SidebarContent({ showPinToggle }: SidebarContentProps = {}) {
  const projectMatch = useMatch({
    from: "/app/projects/$projectId",
    shouldThrow: false,
  });
  const projectId = projectMatch?.params.projectId;

  if (projectId) {
    return (
      <ProjectSidebarContent
        projectId={projectId}
        showPinToggle={showPinToggle}
      />
    );
  }
  return <GlobalSidebarContent showPinToggle={showPinToggle} />;
}

/**
 * The app sidebar. Renders one of three faces depending on `mode`:
 *
 *   - "drawer"   — phone; temporary overlay drawer.
 *   - "rail"     — narrow icon strip with a hover-peek floating overlay
 *                  on top of content.
 *   - "expanded" — full-width sidebar with a Pin/Unpin toggle in the
 *                  brand row.
 *
 * The peek overlay is rendered *next to* the rail, layered on top of the
 * page content via z-index so it doesn't push the working surface.
 */
export function Sidebar({ mode, drawerOpen, onDrawerClose }: SidebarProps) {
  if (mode === "drawer") {
    return (
      <Drawer
        variant="temporary"
        open={drawerOpen}
        onClose={onDrawerClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: SIDEBAR_WIDTH,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: SIDEBAR_WIDTH,
            boxSizing: "border-box",
            bgcolor: "surface.default",
            borderRight: "1px solid",
            borderColor: "border.default",
          },
        }}
      >
        <SidebarContent />
      </Drawer>
    );
  }

  if (mode === "expanded") {
    return (
      <Box
        component="aside"
        sx={{
          width: SIDEBAR_WIDTH,
          flexShrink: 0,
          height: "100%",
          bgcolor: "surface.default",
          borderRight: "1px solid",
          borderColor: "border.default",
          transition: (t) => `width ${t.motion.spring}`,
        }}
      >
        <SidebarContent showPinToggle />
      </Box>
    );
  }

  // mode === "rail" — narrow strip with a hover-peek overlay
  return <RailWithPeek />;
}

function RailWithPeek() {
  const [peeking, setPeeking] = useState(false);
  const openTimer = useRef<number | null>(null);
  const closeTimer = useRef<number | null>(null);
  const isPointerCoarse = useMediaQuery("(pointer: coarse)");

  // Peek is a hover-only affordance — it would feel hostile on a
  // touchscreen where there's no hover state. On coarse pointers the
  // user explicitly clicks the pin button to reveal the full nav.
  const allowPeek = !isPointerCoarse;

  useEffect(() => {
    return () => {
      if (openTimer.current) window.clearTimeout(openTimer.current);
      if (closeTimer.current) window.clearTimeout(closeTimer.current);
    };
  }, []);

  const handleEnter = () => {
    if (!allowPeek) return;
    if (closeTimer.current) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    if (peeking) return;
    openTimer.current = window.setTimeout(() => {
      setPeeking(true);
      openTimer.current = null;
    }, PEEK_OPEN_DELAY_MS);
  };

  const handleLeave = () => {
    if (!allowPeek) return;
    if (openTimer.current) {
      window.clearTimeout(openTimer.current);
      openTimer.current = null;
    }
    closeTimer.current = window.setTimeout(() => {
      setPeeking(false);
      closeTimer.current = null;
    }, PEEK_CLOSE_DELAY_MS);
  };

  // Auto-close the peek on navigation. Without this, clicking a nav
  // item that lands the user on another rail-mode surface (e.g.
  // /projects/A/chat → /projects/B/chat) would leave the peek showing
  // until the next mouse-leave; closing on route change keeps the
  // peek behaviour aligned with the user's intent ("I'm done here").
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  useEffect(() => {
    setPeeking(false);
  }, [pathname]);

  return (
    <Box
      component="aside"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      sx={{
        position: "relative",
        width: SIDEBAR_RAIL_WIDTH,
        flexShrink: 0,
        height: "100%",
      }}
    >
      <Box
        sx={{
          width: SIDEBAR_RAIL_WIDTH,
          height: "100%",
          bgcolor: "surface.default",
          borderRight: "1px solid",
          borderColor: "border.default",
        }}
      >
        <SidebarRail />
      </Box>

      <Box
        aria-hidden={!peeking}
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          height: "100%",
          width: SIDEBAR_WIDTH,
          bgcolor: "surface.default",
          borderRight: "1px solid",
          borderColor: peeking ? "border.accent" : "border.default",
          boxShadow: peeking ? "12px 0 32px -16px rgba(0, 0, 0, 0.55)" : "none",
          transform: peeking ? "translateX(0)" : "translateX(-12px)",
          opacity: peeking ? 1 : 0,
          pointerEvents: peeking ? "auto" : "none",
          zIndex: (t) => t.zIndex.drawer + 1,
          transition: (t) =>
            `transform ${t.motion.spring}, opacity ${t.motion.base}, border-color ${t.motion.fast}, box-shadow ${t.motion.base}`,
        }}
      >
        <SidebarContent showPinToggle />
      </Box>
    </Box>
  );
}
